import { useNavigate } from 'react-router-dom';

import { Button } from './ui.jsx';

/**
 * The credit-earning journey.
 *
 * Three plain steps a user follows to collect Cortis credits, each one a real
 * on-chain action from their own wallet: map yourself, deploy an agent, then
 * generate a workflow. The same header renders on every step so the path is
 * obvious, and each screen gets a Next control at the bottom that carries the
 * user straight to the following step.
 */
export const EARN_STEPS = [
  { path: '/map', label: 'Map me', blurb: 'Describe yourself and build your map.' },
  { path: '/deploy', label: 'Deploy agent', blurb: 'Spin up an agent that works your way.' },
  { path: '/build', label: 'Generate workflow', blurb: 'Put your agents on a schedule.' },
];

export function stepIndex(path) {
  return EARN_STEPS.findIndex((s) => s.path === path);
}

/**
 * The progress header shown at the top of each earn step. Numbered, with the
 * current step lit and completed steps marked, so a user always sees where they
 * are and what is left.
 */
export function JourneySteps({ current }) {
  const navigate = useNavigate();
  return (
    <div className="journey" role="group" aria-label="Earn Cortis credits in three steps">
      <div className="journey__intro">
        <span className="journey__kicker">Earn Cortis credits</span>
        <span className="journey__hint">Three steps. Each one is a signed on-chain action that earns credits.</span>
      </div>
      <ol className="journey__steps">
        {EARN_STEPS.map((s, i) => {
          const status = i < current ? 'done' : i === current ? 'active' : 'todo';
          return (
            <li key={s.path} className={`journey__step journey__step--${status}`}>
              <button
                type="button"
                className="journey__step-btn"
                onClick={() => navigate(s.path)}
                aria-current={status === 'active' ? 'step' : undefined}
              >
                <span className="journey__step-num" aria-hidden="true">{status === 'done' ? '✓' : i + 1}</span>
                <span className="journey__step-text">
                  <span className="journey__step-label">{s.label}</span>
                  <span className="journey__step-blurb">{s.blurb}</span>
                </span>
              </button>
              {i < EARN_STEPS.length - 1 && <span className="journey__arrow" aria-hidden="true">→</span>}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * The bottom control that advances the user to the next earn step. On the last
 * step it points to Agents so the user lands on what they just built. `done`
 * marks this step's action as finished so the button reads as a natural next.
 */
export function JourneyNext({ current, done = false }) {
  const navigate = useNavigate();
  const isLast = current >= EARN_STEPS.length - 1;
  const next = isLast ? { path: '/agents', label: 'View your agents' } : EARN_STEPS[current + 1];

  return (
    <div className="journey-next">
      <div className="journey-next__label">
        {isLast ? (
          <span>That is all three steps. Your credits are recorded on chain.</span>
        ) : (
          <span>
            {done ? 'Done. ' : ''}Next: <strong>{next.label}</strong> — {next.blurb}
          </span>
        )}
      </div>
      <Button variant="primary" onClick={() => navigate(next.path)}>
        {isLast ? next.label : `Next: ${next.label} →`}
      </Button>
    </div>
  );
}
